// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { DocumentState, DocumentType } from '../utils/types';
import { CertificationServiceApiUrl } from '../config/config';
import { PublicAPIService } from '../utils/publicApiService';

export interface CertificateServiceResponse {
  success: boolean;
  data?: Blob;
  error?: string;
}

export class CertificateService {
  public static async generateCertificatePDF(document: DocumentState, documentType: DocumentType): Promise<CertificateServiceResponse> {
    // Only handle certificates - awards and quarterly awards should be handled differently
    if (documentType !== 'certificate') {
      return {
        success: false,
        error: 'Only certificate generation is supported by this service. Awards and quarterly awards should be handled by the frontend.',
      };
    }

    try {
      // Call the Ballerina service endpoint which will handle the microservice integration
      if ('displayName' in document) {
        const certificationId = document.contact?.certificationId;
        const certificateId = document.id;
        if (!certificationId || !certificateId) {
          return {
            success: false,
            error: 'Certification ID and Certificate ID are required for certificate generation',
          };
        }
        const url = `${CertificationServiceApiUrl}/certificate/${certificationId}/${certificateId}`;

        const response = await PublicAPIService.getInstance().get(url, {
          headers: {
            'Accept': 'application/pdf',
          },
          responseType: 'blob',
        });

        if (response.status !== 200) {
          return {
            success: false,
            error: 'Failed to generate PDF',
          };
        }

        const pdfBlob = response.data;
        return {
          success: true,
          data: pdfBlob,
        };
      } else {
        return {
          success: false,
          error: 'Invalid document format for certificate generation.',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error?.response?.data?.message || error?.message || 'Network error',
      };
    }
  }

  public static createPdfUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
  }

  public static revokePdfUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  public static downloadPdf(blob: Blob, filename: string): void {
    const url = this.createPdfUrl(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    this.revokePdfUrl(url);
  }
}
